import type { ApolloError } from '@apollo/client';
import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { PageFunctionType } from '@shared/core/analytics/telemetry/types';
import type { DesignLayoutType, EventPageVisibility, LeadStateV2 } from '@graphql/generated-base';
import { getViewportSize } from '@shared/core/analytics/segment/eventEnrichment';
import type { GuestSiteQueryParams } from './packages/layout-engine/common/utils/useGuestSiteQueryParams';
import type { PagePrefix } from '@shared/core/analytics/context';
import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import type { HeaderMotionMedia, HeaderMotionTypeId } from '@shared/core/website/headerMotion/headerMotion.types';

export type PagesForTelemetry =
  | 'welcome'
  | 'faq'
  | 'story'
  | 'travel'
  | 'tidbits'
  | 'registrylinks'
  | 'schedule'
  | 'moments'
  | 'weddingparty'
  | 'rsvp'
  | 'accommodations'
  | 'custom';

export type MarriottRoomBlockBannerTelemetryInfo = Readonly<{
  placeId: string;
  hotelName: string;
  daysLeft: number;
}>;

export type SecurityInfo = {
  requiresPassword: boolean;
  isUnlocked?: boolean;
};

export type EnabledPagesInfo = {
  slug: string;
  title: string;
  visibility: EventPageVisibility;
};

export type RoomBlocksInfo = {
  leadState?: LeadStateV2;
  availableCount: number;
  daysUntilSoonestCutOff?: number;
};

export type GuestSiteLoadTelemetry = {
  layout: DesignLayoutType;
  userRole: string;
  security: SecurityInfo;
  enabledPages: EnabledPagesInfo[];
  daysUntilEvent?: number;
} & GuestSiteQueryParams;

const createPageTelemetryData = (defaultPageName: string, action: string) => (userRole: string, args: GuestSiteQueryParams, duration?: string) => ({
  category: 'wedding',
  pagePrefix: 'guest' as PagePrefix,
  page: duration ? `${duration}.${defaultPageName}` : defaultPageName,
  action,
  extraPageInfo: {
    role: userRole,
    ...args,
  },
});

const pages: { [tab in PagesForTelemetry]: PageFunctionType } = {
  welcome: createPageTelemetryData('Welcome', 'ViewWelcome'),
  faq: createPageTelemetryData('faq', 'ViewFAQ'),
  story: createPageTelemetryData('story', 'ViewStory'),
  tidbits: createPageTelemetryData('tidbits', 'ViewTidbits'),
  travel: createPageTelemetryData('travel', 'ViewTravel'),
  registrylinks: createPageTelemetryData('registrylinks', 'ViewRegistryLinks'),
  schedule: createPageTelemetryData('schedule', 'ViewSchedule'),
  moments: createPageTelemetryData('moments', 'ViewMoments'),
  weddingparty: createPageTelemetryData('weddingParty', 'ViewWeddingParty'),
  rsvp: createPageTelemetryData('RSVP', 'ViewRSVP'),
  accommodations: createPageTelemetryData('accommodations', 'ViewAccommodations'),
  custom: createPageTelemetryData('custom', 'ViewCustom'),
};

export const guestSiteTelemetry = createTelemetryObject({
  actions: {
    bookYourStayPageViewed: (args: GuestSiteQueryParams) => ({
      category: 'wedding',
      pagePrefix: 'guest',
      page: 'book-your-stay',
      action: 'TravelButtonInteracted',
      extraInfo: { ...args, name: 'viewBookYourStay' },
    }),

    trackError: (mutation: string, error: ApolloError | Error | unknown, extra = {}) => ({
      category: 'wedding',
      action: 'Mutation',
      actionType: 'error',
      extraInfo: { error, mutation, ...extra },
    }),

    adminPreviewClick: () => ({
      category: 'admin',
      action: 'OpenWebsite',
      actionType: 'click',
      label: 'CharmPreview',
    }),

    announcementBannerChange: (title: string, status: 'Shown' | 'Dismissed') => ({
      action: 'AnnouncementBannerStatus',
      category: 'wedding',
      label: title,
      extraInfo: {
        status: status,
      },
    }),

    // Rides on the established (Amplitude-whitelisted) TravelButtonInteracted event — travel
    // telemetry discriminates sub-events via `extraInfo.name` rather than new top-level actions.
    weddingCodeBannerViewed: (weddingCode: string) => ({
      action: 'TravelButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'weddingCodeBannerViewed',
        weddingCode,
        // Explicit mobile/desktop viewport bucket (<=1024px) for the banner funnel — named for the
        // helper, not "deviceType", since tablets/narrow windows land in 'mobile'. Event id + raw
        // viewport dims already ride on every event via the shared analytics enrichment layer.
        viewportSize: getViewportSize(),
      },
    }),

    weddingCodeBannerClicked: (weddingCode: string, utm?: WritableUtmParameters) => ({
      action: 'TravelButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'weddingCodeBannerClicked',
        weddingCode,
        viewportSize: getViewportSize(),
        ...utm,
      },
    }),

    // Marriott room block banner. Rides TravelButtonInteracted like the wedding-code banner.
    // Exposed: the experiment was read for a guest who qualifies (both arms), with the assigned variant.
    // Viewed: the strip actually rendered.
    marriottRoomBlockBannerExposed: (info: MarriottRoomBlockBannerTelemetryInfo, variant: string | undefined, weddingCodeFirst: boolean) => ({
      action: 'TravelButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'marriottRoomBlockBannerExposed',
        ...info,
        variant: variant ?? 'none',
        weddingCodeFirst,
      },
    }),

    marriottRoomBlockBannerViewed: (info: MarriottRoomBlockBannerTelemetryInfo) => ({
      action: 'TravelButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'marriottRoomBlockBannerViewed',
        ...info,
        viewportSize: getViewportSize(),
      },
    }),

    marriottRoomBlockBannerClicked: (info: MarriottRoomBlockBannerTelemetryInfo, utm?: WritableUtmParameters) => ({
      action: 'TravelButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'marriottRoomBlockBannerClicked',
        ...info,
        viewportSize: getViewportSize(),
        ...utm,
      },
    }),

    marriottRoomBlockBannerDismissed: (info: MarriottRoomBlockBannerTelemetryInfo) => ({
      action: 'TravelButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'marriottRoomBlockBannerDismissed',
        ...info,
      },
    }),

    /**
     * A header Motion opening (`motionType`: Cinematic, Bloom, ...) started playing on the live guest
     * site, at most once per mount.
     * Never fires when it settles straight to its end frame (already seen, reduced motion), in previews
     * (`data-hm-always="true"`), or on replays.
     *
     * `ButtonInteracted`, not the banners' `TravelButtonInteracted`: this is a header impression, not a
     * click and not travel, and the travel action would file it into the travel funnels.
     */
    headerMotionOpeningPlayed: (motionType: HeaderMotionTypeId, media: HeaderMotionMedia) => ({
      action: 'ButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'headerMotionOpeningPlayed',
        motionType,
        media,
        viewportSize: getViewportSize(),
      },
    }),

    inAppBrowserBannerChange: (status: 'Shown' | 'Dismissed' | 'OpenClicked', app: string, platform: string) => ({
      action: 'InAppBrowserBannerStatus',
      category: 'wedding',
      label: app,
      extraInfo: {
        status,
        platform,
      },
    }),

    notPublishedBanner: (label: 'Shown' | 'Dismissed' | 'Published') => ({
      action: 'NotPublishedWarningStatus',
      category: 'wedding',
      label,
    }),

    // MENU
    navMenuOpenedChange: (label: 'opened' | 'closed') => ({
      action: 'NavMenuOpened',
      category: 'wedding',
      label,
    }),

    // `utm` is the same object stamped on the link's href, so the event and the resulting
    // page-view can't disagree about where the click came from.
    navMenuItemClicked: (pageClicked: string, utm?: WritableUtmParameters) => ({
      action: 'ButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'GuestSiteNavClicked',
        pageClicked,
        ...utm,
      },
    }),

    mobileTopBarNavLinkClicked: (pageSlug: string, utm?: WritableUtmParameters) => ({
      action: 'ButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'MobileTopBarNavClicked',
        pageClicked: pageSlug,
        ...utm,
      },
    }),

    mobileTopBarNavMenuOpened: () => ({
      action: 'ButtonInteracted',
      category: 'wedding',
      extraInfo: {
        name: 'MobileTopBarNavMenuOpened',
      },
    }),

    // Photo Editor
    photoEditorOpened: (label: 'opened' | 'closed') => ({
      action: 'PagePhotoEditorStatus',
      category: 'wedding',
      label,
    }),

    photoEditorChangePhotoClicked: () => ({
      action: 'PagePhotoEditorChangePhoto',
      actionType: 'click',
      category: 'wedding',
    }),

    photoEditorUpdateFocus: (previewsFocus: unknown, currentFocus: unknown) => ({
      action: 'PagePhotoEditorFocusChanged',
      category: 'wedding',
      extraInfo: {
        previewsFocus,
        currentFocus,
      },
    }),

    // Welcome Section Aloha
    welcomeViewDetailsButtonClicked: () => ({
      action: 'WelcomeViewDetailsButtonClicked',
      actionType: 'click',
      category: 'wedding',
    }),

    welcomeRSVPButtonClicked: () => ({
      action: 'WelcomeRSVPButtonClicked',
      actionType: 'click',
      category: 'wedding',
    }),

    welcomeNotYouButtonClicked: () => ({
      action: 'WelcomeRSVPButtonClicked',
      actionType: 'click',
      category: 'wedding',
    }),

    // APP Section
    weHaveAnAppClicked: (app: string) => ({
      action: 'WeHaveAnAppClicked',
      actionType: 'click',
      category: 'wedding',
      label: app,
    }),

    editEventClicked: () => ({
      action: 'EditEventClicked',
      actionType: 'click',
      category: 'wedding',
    }),

    // Footer
    footerItemClicked: (item: string) => ({
      action: 'FooterItemClicked',
      actionType: 'click',
      category: 'wedding',
      label: item,
    }),

    websiteVisibilityPublish: (eventId: string) => ({
      category: 'adminWysiwyg',
      action: 'PublishGoLive',
      extraInfo: {
        eventId,
      },
    }),
  },
  pages,
});

const { TelemetryProvider, useTelemetry: useGuestSiteTelemetry, enrichTelemetryExtraInfo } = createTelemetry(guestSiteTelemetry);
export { TelemetryProvider, useGuestSiteTelemetry, enrichTelemetryExtraInfo };
